import type { DocumentMapping } from "@/configuration/document-mappings/types";
import type { ConfigSubdomain } from "@/configuration/tenant-ids";
import type { AthenaInsuranceMapping } from "@/src/lib/ehr/athena/athena-insurance-mapping";
import type { AthenaAppointmentType, AthenaDocument, AthenaDocumentApiType } from "@/src/lib/ehr/athena/athena-types";
import type { AppointmentType } from "@/src/models/appointment";
import type { ConfirmedAthenaInsurancePlan } from "@/src/models/athena-insurance-plan";
import type { Classification } from "@/src/models/constants/classifications";
import type { Coverage } from "@/src/models/coverage";
import type { Referral } from "@/src/models/documents/referral";
import type { Group } from "@/src/models/group";
import type { Patient } from "@/src/models/patient";
import type { Practitioner } from "@/src/models/practitioner";
import type { Section } from "@/src/models/section";
import type { StediEligibilityCheck } from "@/src/models/stedi-eligibility-check";

export const ATHENA_CONFIG_VERSION = 1;

export enum AppointmentTypePurpose {
  BOOKED = "booked",
  OPEN = "open",
}

/** Source used to select the patient's primary department. */
export enum PatientPrimaryDepartment {
  DOCUMENT_SOURCE = "document_source",
  RECEIVING_LOCATION = "receiving_location",
}

export type AthenaDepartmentConfig = {
  name: string;
  assignee: string | null;
  providerGroup?: string | null;
};

export type AthenaDepartmentRedirects = Readonly<Record<string, string>>;

export type AthenaConsentConfig = {
  medicationhistoryconsentverified: boolean | null;
  isprivacynoticeonfile: boolean | null;
  consenttocall: boolean | null;
  consenttotext: boolean | null;
};

export enum AthenaReferringProviderMatchStrategy {
  LEGACY = "legacy",
  NPI_FIRST = "npi_first",
  NPI_FIRST_IGNORE_ZIP = "npi_first_ignore_zip",
}

export type CancellationPatientCaseConfig = {
  assignee: string;
  departmentId: string;
};

export type AdditionalAppointmentPatientCaseConfig = {
  assignee: string;
};

/** Policy for booking alongside, or cancelling, an established patient's upcoming appointment. */
export type AppointmentPatientCasePolicy = {
  // Offer reschedule-vs-add-new instead of blocking a new booking when an appointment already exists.
  allowNewWithUpcoming: boolean;
  // Cancellation patient case (assignee + department) for a provider; null opts out.
  cancellationCase?: (providerEhrId: string | null) => CancellationPatientCaseConfig | null;
  // Additional-booking patient case; the caller supplies the booked appointment's department.
  additionalAppointmentCase?: (providerEhrId: string | null) => AdditionalAppointmentPatientCaseConfig | null;
};

export type AthenaSchedulingBlockingOrderRule = Readonly<{
  id: string;
  match:
    | Readonly<{ type: "any_active_order" }>
    | Readonly<{ keywords: readonly string[]; type: "description_contains" }>;
  reason: string;
}>;

export type AthenaSchedulingBlockingOrderPolicy = Readonly<{
  enabledGroupIds: readonly string[];
  terminalStatuses: readonly string[];
  rulesByDepartmentEhrId: Readonly<Record<string, readonly AthenaSchedulingBlockingOrderRule[]>>;
}>;

export type AthenaReferralSource = {
  referralsourceid: string;
  referralsourceother: string;
};

export type AthenaDocumentUploadInputs = {
  mapping: DocumentMapping;
  documentApiType: AthenaDocumentApiType;
  documentSubclass: string;
  autoClose: boolean;
};

export type ResolveAthenaDocumentUploadInputsParams = {
  baseMapping: DocumentMapping;
  classification: Classification;
  section: Section;
  departmentId: string | null;
  providerEhrId: string | null;
};

export type AthenaDocumentUploadInputResolver = (
  params: ResolveAthenaDocumentUploadInputsParams
) => AthenaDocumentUploadInputs;

export type ResolveAthenaDocumentAssigneeParams = {
  referral: Referral;
  document: AthenaDocument | null;
  departmentRedirects: AthenaDepartmentRedirects;
};

export type AthenaDocumentAssigneeResolver = (params: ResolveAthenaDocumentAssigneeParams) => string | null;

export type AthenaTicklerCreationPolicy = (departmentId: string) => boolean;

export type AthenaReferralAssignment = {
  providerEhrId: string | null;
  departmentId: string | null;
};

export type ResolveAthenaReferralAssignmentParams = {
  group: Group;
  referral: Referral;
  receivingProviderEhrId: string | null;
  patientPrimaryDepartmentId: string | null;
  patientPrimaryProviderId: string | null;
};

export type AthenaReferralAssignmentResolver = (
  params: ResolveAthenaReferralAssignmentParams
) => Promise<AthenaReferralAssignment>;

export type AthenaExistingCoverage = {
  insurancePackageId: string | null;
  subscriberId: string | null;
  sequence: number | null;
  casePolicy: boolean;
  ehrCancelledAt: string | null;
};

export type AthenaCoverageWriteReviewInput = {
  coverage: Coverage;
  insurancePackageId: string;
  casePolicy: boolean;
  existing: readonly AthenaExistingCoverage[];
  eligibility: StediEligibilityCheck | null;
  confirmedPlans: readonly ConfirmedAthenaInsurancePlan[];
  patient: Patient;
  group: Group;
  now: string;
};

export type AthenaCoverageWriteConfig = {
  review: (input: AthenaCoverageWriteReviewInput) => string | null;
  memberId?: (coverage: Coverage, memberId: string) => string;
  policyNumber?: (coverage: Coverage) => string | undefined;
};

export type AthenaTenantConfig = {
  version: typeof ATHENA_CONFIG_VERSION;
  subdomain: ConfigSubdomain;
  core: {
    practiceId: number;
    departments: Record<string, AthenaDepartmentConfig>;
    patientPrimaryDepartment: PatientPrimaryDepartment;
    defaultReceivingLocationEhrId: string | null;
  };
  patient: {
    consent: AthenaConsentConfig | null;
    referralSource: (
      referringProvider: Practitioner | null | undefined,
      isExistingPatient: boolean
    ) => AthenaReferralSource | null;
    referringProviderMatchStrategy: AthenaReferringProviderMatchStrategy;
    updateInsurance: boolean;
    updatePatient: boolean;
    createReferringProvider: boolean;
    overridePhoneNumbers: boolean;
  };
  insurance: {
    /** Department whose current coverage is hydrated during patient lookup/scheduling; null keeps legacy reads. */
    patientCoverageDepartmentId: string | null;
    mapping: AthenaInsuranceMapping | null;
    payerCodes: Record<string, string>;
    resolveCasePolicy: (memberId: string, coverage: Coverage) => number | null;
    /**
     * When true, an unmatched plan name skips the coverage write and flags it for ops instead of
     * falling back to the payer's `isDefault` plan entry.
     */
    strictPlanMatching: boolean;
    /**
     * When set, coverage writes fail closed. Review must pass, audit persist and ehrId link
     * are required, and in-place Athena insurance or case-policy creation is refused when
     * the chart already has policies.
     */
    coverageWrite?: AthenaCoverageWriteConfig;
  };
  documents: {
    lookbackDays: number;
    referralDocumentTypeId: (isNewPatient: boolean) => number | null;
    note: (referral?: Referral | null, document?: AthenaDocument | null) => string;
    priority: (referral: Referral) => string;
  };
  patientCases: {
    createWithUrgency: (referral: Referral) => boolean;
    caseDepartment: (registrationDepartmentId: string, assignmentDepartmentId: string) => string;
    providerAssignment: (params: {
      departmentId: string;
      primaryProviderId: string | null;
      referral: Referral;
      assignedProviderEhrId: string | null;
    }) => string | null;
    shouldCreate: (params: {
      departmentId: string;
      primaryProviderId: string | null;
      referral: Referral;
      assignedProviderEhrId: string | null;
    }) => boolean;
    assignee: (departmentId: string, referral: Referral) => string | null;
    note: (referral: Referral) => string;
    subject: (referral: Referral, existingAppointmentDate?: string | null, departmentId?: string | null) => string;
    source: string;
    documentSubclass: string;
    outboundOnly: boolean;
    lookbackDays: number | null;
    fetchExistingAppointmentForSubject: boolean;
    persistRoutedLocation: boolean;
  };
  scheduling: {
    /** Extra departments authorized only for explicit booked-appointment history reads. */
    additionalAppointmentHistoryDepartmentIds: readonly string[];
    createTicklerWithUrgency: (referral: Referral) => boolean;
    ticklerProviderAssignment: (providerId: string | null, departmentId?: string) => string | null;
    reminderNote: (options: { documentId?: string; referral?: Referral }) => string;
    resolveAppointmentTypeId: (params: {
      group: Group;
      appointmentType: AppointmentType;
      providerEhrId?: string;
      departmentId?: string;
      purpose: AppointmentTypePurpose;
    }) => number;
    appointmentTypes: AthenaAppointmentType[];
    appointmentPatientCasePolicy: AppointmentPatientCasePolicy;
    /** Default eligibility history scope, independent of patient registration and booking locations. */
    eligibilityHistoryDepartmentIds: string[] | null;
    bookedAppointmentType: (providerEhrId: string, appointmentTypeId: number) => AppointmentType | null;
    bookedTypeIdFromOpenTypeId: (
      providerEhrId: string,
      openAppointmentTypeId: string,
      internalAppointmentType: AppointmentType | null
    ) => number | null;
    /** Preserve the selected appointment's authoritative booked type throughout rescheduling. */
    preserveBookedTypeForRescheduling: boolean;
    /** Keep Athena slots whose returned type is a backing template rather than the requested visit type. */
    acceptBackingOpenAppointmentTypes: boolean;
    blockingOrders: AthenaSchedulingBlockingOrderPolicy | null;
  };
};

export type AthenaTenantConfigOverrides = {
  subdomain: ConfigSubdomain;
  core: Omit<AthenaTenantConfig["core"], "defaultReceivingLocationEhrId"> &
    Partial<Pick<AthenaTenantConfig["core"], "defaultReceivingLocationEhrId">>;
  patient?: Partial<AthenaTenantConfig["patient"]>;
  insurance?: Partial<AthenaTenantConfig["insurance"]>;
  documents?: Partial<AthenaTenantConfig["documents"]>;
  patientCases?: Partial<AthenaTenantConfig["patientCases"]>;
  scheduling?: Partial<AthenaTenantConfig["scheduling"]>;
};
